import { selectActiveElement } from "@/store/selections-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { FaroText, selectAncestor } from "@faro-lotv/app-component-toolbox";
import { FaroButton } from "@faro-lotv/flat-ui";
import { isIElementRoomSection } from "@faro-lotv/ielement-types";
import { Stack } from "@mui/material";
import { type JSX } from "react";
import {
  useApplyEditWaypointsChanges,
  useCancelEditWaypoints,
} from "./use-apply-edit-waypoints-changes";

type MiniMapEditWaypointsToolbarProps = {
  /** Callback executed when editing should be stopped */
  onStopEditing(): void;
};

/** @returns Bottom toolbar visible when the edit waypoints mode is active in the minimap */
export function MiniMapEditWaypointsToolbar({
  onStopEditing,
}: MiniMapEditWaypointsToolbarProps): JSX.Element {
  const onCancel = useCancelEditWaypoints(onStopEditing, "minimap");

  const onApply = useApplyEditWaypointsChanges(onStopEditing, "minimap");

  const isActiveWaypointMovable = useAppSelector((state) =>
    selectAncestor(selectActiveElement(state), isIElementRoomSection)(state),
  );

  return (
    <Stack
      direction="row"
      sx={{
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        py: 1,
      }}
    >
      <FaroText variant="labelM" dark>
        {isActiveWaypointMovable
          ? "Drag the active waypoint to reposition it"
          : "Select a 360° waypoint to reposition it"}
      </FaroText>

      <Stack direction="row" sx={{ gap: 1, flexShrink: 0 }}>
        <FaroButton
          aria-label="cancel waypoint changes"
          variant="ghost"
          onClick={onCancel}
          size="s"
          dark
        >
          Cancel
        </FaroButton>
        <FaroButton
          aria-label="apply waypoint changes"
          onClick={onApply}
          size="s"
          dark
        >
          Apply changes
        </FaroButton>
      </Stack>
    </Stack>
  );
}
